/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { InfoIcon } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useId, useState } from "react"
import { isMalware } from "../../features/application-detail/application-apk"
import type { ApplicationApk } from "../../features/application-detail/application-detail-types"
import type { ValueOf } from "../../utils/types"
import { Alert, AlertDescription } from "../primitives/alert"
import { Card } from "../primitives/card"
import { Label } from "../primitives/label"
import { Switch } from "../primitives/switch"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../primitives/table"
import { ToggleGroup, ToggleGroupItem } from "../primitives/toggle-group"
import { ApplicationApkRow } from "./application-apk-row"

/**
 * The APK list shown by the tab.
 */
const Filter = {
    Malware: "malware",
    Collected: "collected"
} as const
type Filter = ValueOf<typeof Filter>

/**
 * Makes the pressed filter match the default button styling from the wireframe.
 *
 * Use the same `aria-pressed:` variant as the toggle's base styles so
 * tailwind-merge replaces the pressed-state classes instead of keeping both.
 */
const TOGGLE_CLASSES =
    "uppercase aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-primary-foreground"

interface Props {
    /**
     * Active APKs, in API order.
     */
    apks: ApplicationApk[]

    /**
     * Removed APKs, in API order.
     */
    removedApks: ApplicationApk[]
}

/**
 * Displays security-relevant APKs collected from the device.
 *
 * Malware shows only APKs with a `MALWARE` threat level; Collected shows every
 * APK returned by the API. Both lists preserve API order, and each button shows
 * the count of its list.
 *
 * Show removed adds the matching removed APKs below the active rows. The active
 * filter also applies to removed APKs, and the button counts include active rows
 * only.
 *
 * The filter and the Show removed switch are local state, so they reset when the tab
 * unmounts.
 *
 * @example
 * <TabsContent value="malware">
 *     <ApplicationDetailMalware apks={vm.applicationApks} removedApks={vm.removedApplicationApks} />
 * </TabsContent>
 */
export const ApplicationDetailMalware = observer(({ apks, removedApks }: Props) => {
    const [filter, setFilter] = useState<Filter>(Filter.Malware)
    const [showRemoved, setShowRemoved] = useState(false)
    const showRemovedId = useId()

    const malware = apks.filter(isMalware)
    const showsMalware = filter === Filter.Malware
    const shownApks = showsMalware ? malware : apks
    const shownRemovedApks = showsMalware ? removedApks.filter(isMalware) : removedApks

    const selectFilter = (value: string[]) => {
        // Keep the current list when its button is pressed again, since the toggle can
        // otherwise clear the selection and leave no list selected.
        const next = Object.values(Filter).find((option) => option === value[0])
        if (next) {
            setFilter(next)
        }
    }

    return (
        <div className="grid gap-4 border bg-background px-3 py-6">
            <h3 className="m-0 text-xl font-semibold">Mobile Malware</h3>

            <div className="flex flex-wrap items-center justify-between gap-2">
                <ToggleGroup
                    variant="outline"
                    size="sm"
                    aria-label="Show"
                    value={[filter]}
                    onValueChange={selectFilter}
                >
                    <ToggleGroupItem value={Filter.Malware} className={TOGGLE_CLASSES}>
                        Malware ({malware.length})
                    </ToggleGroupItem>
                    <ToggleGroupItem value={Filter.Collected} className={TOGGLE_CLASSES}>
                        Collected ({apks.length})
                    </ToggleGroupItem>
                </ToggleGroup>

                <div className="flex items-center gap-2">
                    <Label htmlFor={showRemovedId} className="uppercase">
                        Show removed
                    </Label>
                    <Switch id={showRemovedId} checked={showRemoved} onCheckedChange={setShowRemoved} />
                </div>
            </div>

            <Alert className="border-sky-500/10 bg-sky-500/5 *:[svg]:text-sky-500">
                <InfoIcon />
                <AlertDescription>
                    {showsMalware ? describeMalware(malware.length) : describeCollected(apks.length)}
                </AlertDescription>
            </Alert>

            <Card className="py-0">
                {/* Only APK rows open a detail, so only they keep the hover state. */}
                <Table className="[&_thead_tr]:hover:bg-transparent">
                    <colgroup>
                        <col className="w-1/5" />
                        <col className="w-1/10" />
                        <col className="w-3/20" />
                        <col className="w-1/5" />
                        <col className="w-1/5" />
                        <col className="w-3/20" />
                        <col className="w-px" />
                    </colgroup>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Application</TableHead>
                            <TableHead>
                                <span className="hidden xl:inline">Affected </span>Devices
                            </TableHead>
                            <TableHead>Installation</TableHead>
                            <TableHead>Signatures</TableHead>
                            <TableHead>Categories</TableHead>
                            <TableHead>Risk Level</TableHead>
                            <TableHead />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {shownApks.length > 0
                            ? shownApks.map((apk) => <ApplicationApkRow key={apk.id} apk={apk} />)
                            : renderEmptyRow(showsMalware ? "No malware" : "No collected applications")}
                    </TableBody>
                    {/* Keep removed rows in a separate group below the active rows. */}
                    {showRemoved &&
                        renderRemoved(
                            shownRemovedApks,
                            showsMalware ? "No removed malware" : "No removed applications"
                        )}
                </Table>
            </Card>
        </div>
    )
})

/**
 * Builds the info alert text for the Malware list.
 *
 * @param count - Number of APKs in the list.
 * @returns The alert text with the count emphasized.
 */
function describeMalware(count: number) {
    return (
        <>
            This view shows <strong>{count}</strong> {count === 1 ? "application that was" : "applications that were"}{" "}
            classified as malware.
        </>
    )
}

/**
 * Builds the info alert text for the Collected list.
 *
 * The API returns only security-relevant APKs, not every app on the device.
 *
 * @param count - Number of APKs in the list.
 * @returns The alert text with the count emphasized.
 */
function describeCollected(count: number) {
    return (
        <>
            This view shows <strong>{count}</strong> security-relevant {count === 1 ? "application" : "applications"}{" "}
            collected from the device.
        </>
    )
}

/**
 * Renders the removed section below the active APK rows.
 *
 * Shows the matching APKs or an empty-state row when none match the filter.
 *
 * @param apks - Removed APKs matching the current filter.
 * @param emptyMessage - Message shown when no APKs match.
 * @returns A `<tbody>` containing the section heading and APK rows.
 */
function renderRemoved(apks: ApplicationApk[], emptyMessage: string) {
    return (
        <TableBody className="border-t">
            <TableRow className="hover:bg-transparent">
                <TableCell
                    colSpan={7}
                    className="bg-muted/50 py-1.5 text-xs font-semibold tracking-[0.25em] text-muted-foreground uppercase"
                >
                    Removed
                </TableCell>
            </TableRow>
            {apks.length > 0
                ? apks.map((apk) => <ApplicationApkRow key={apk.id} apk={apk} />)
                : renderEmptyRow(emptyMessage)}
        </TableBody>
    )
}

/**
 * Renders an empty-state row for an APK list.
 *
 * Also shown before the first response arrives, since the view model returns
 * an empty array until the data loads.
 *
 * @param message - Message describing the empty list.
 * @returns A row spanning all seven columns.
 */
function renderEmptyRow(message: string) {
    return (
        <TableRow className="hover:bg-transparent">
            <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                {message}
            </TableCell>
        </TableRow>
    )
}
